import { useState } from "react";
import { Link } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DEFAULT_PROMOTION_RULES, DEFAULT_SKILL_RULES, type ShareLevel } from "@krut/shared";
import { api, unwrap } from "../api.ts";
import { useBrand, useGroups } from "../hooks.ts";
import { Badge, Button, Card, ErrorText, PageHeader, Select, Spinner, Switch } from "../components/ui.tsx";
import { knowledgePath, LEVEL_LABEL, LEVELS } from "../components/knowledge.tsx";
import { Icon } from "../components/icons.tsx";

const rulesBox =
  "w-full rounded-lg border border-[var(--line)] bg-[var(--panel)] px-3 py-2 font-mono text-xs leading-relaxed outline-none focus:border-[var(--color-accent)] focus:ring-2 focus:ring-[var(--color-accent)]/20";

/**
 * Admin → Knowledge → Sharing rules: what assistants may share and how far, in one place. The company's
 * rules for memories and skills, the switches, and every group's level (each group's own rules stay on
 * its page, linked from the table).
 */
export function SharingRulesPage() {
  const brand = useBrand();
  const qc = useQueryClient();
  const memory = useQuery({ queryKey: ["memory"], queryFn: () => unwrap(api.admin.memory.$get()) });
  const skills = useQuery({ queryKey: ["skills-admin"], queryFn: () => unwrap(api.admin.skills.$get()) });
  const saveMemory = useMutation({
    mutationFn: (json: { companyMemoryEnabled?: boolean; promotionEnabled?: boolean; promotionRules?: string }) => unwrap(api.admin.memory.$put({ json })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["memory"] }),
  });

  if (memory.isPending || skills.isPending) return <div className="grid place-items-center py-16"><Spinner /></div>;
  if (memory.error || skills.error) return <ErrorText error={memory.error ?? skills.error} />;
  const m = memory.data;
  const model = m.settings.memoryModel ? (m.models.find((x) => x.id === m.settings.memoryModel)?.label ?? m.settings.memoryModel) : "automatic";

  return (
    <>
      <PageHeader
        title="Sharing rules"
        description="What people's assistants may share with colleagues, and how far: within the person's group, or with the whole company. The company's rules are here; each group's level is in the table below and its own rules on the group's page."
      />
      {!m.engineConfigured && (
        <div className="mb-6 rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
          The memory engine isn't connected to {brand.name} yet (missing system key), so memories can't be shared or tested.
        </div>
      )}

      <h2 className="mb-3 text-base font-semibold">Memories</h2>
      <Card className="mb-4 p-5">
        <div className="grid gap-x-8 sm:grid-cols-2">
          <Switch
            label="Assistants recall company memory"
            hint="Every assistant also searches what was shared with its person's group and the company. Results are labelled as company knowledge."
            checked={m.settings.companyMemoryEnabled}
            disabled={saveMemory.isPending}
            onChange={(v) => saveMemory.mutate({ companyMemoryEnabled: v })}
          />
          <Switch
            label="Share memories automatically"
            hint="Every 10 minutes, new memories from people whose group allows it are checked against the rules. Only memories formed after this is switched on are considered."
            checked={m.settings.promotionEnabled}
            disabled={saveMemory.isPending}
            onChange={(v) => saveMemory.mutate({ promotionEnabled: v })}
          />
        </div>
        <p className="mt-3 text-xs text-[var(--muted)]">
          {m.settings.promotionEnabledAt && <>Sharing memories formed since {new Date(m.settings.promotionEnabledAt).toLocaleString()}. </>}
          Memory engine model: <b className="text-[var(--text)]">{model}</b> (
          <Link to="/admin/assistants/models" className="text-[var(--color-accent)] hover:underline">change in Models &amp; providers</Link>).
        </p>
        <ErrorText error={saveMemory.error} />
      </Card>
      <MemoryRules key={m.settings.promotionRules} saved={m.settings.promotionRules} save={saveMemory} canTest={m.engineConfigured} />

      <h2 className="mt-8 mb-3 text-base font-semibold">Skills</h2>
      <SkillRules key={JSON.stringify(skills.data.settings)} settings={skills.data.settings} groupsAllowed={skills.data.groupsAllowedToPublish} onSaved={() => void qc.invalidateQueries({ queryKey: ["skills-admin"] })} />

      <h2 className="mt-8 mb-1 text-base font-semibold">Each group</h2>
      <p className="mb-3 text-sm text-[var(--muted)]">How far each group's memories and skills may travel. A group's own rules say what stays with the team; edit them on the group's page.</p>
      <GroupLevels />
    </>
  );
}

function MemoryRules({ saved, save, canTest }: { saved: string; save: { mutate: (j: { promotionRules: string }) => void; isPending: boolean; isSuccess: boolean }; canTest: boolean }) {
  const [rules, setRules] = useState(saved);
  return (
    <Card className="p-5">
      <h3 className="mb-1 text-sm font-semibold">Company rules for memories</h3>
      <p className="mb-3 text-sm text-[var(--muted)]">
        Plain-language instructions for the AI that decides what may be shared at all, and what counts as company-wide knowledge. It rewrites each shared memory without personal detail; when unsure, it keeps the memory private.
      </p>
      <textarea rows={12} value={rules} onChange={(e) => setRules(e.target.value)} className={rulesBox} aria-label="Company rules for memories" />
      <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
        <button type="button" className="text-sm text-[var(--color-accent)] hover:underline" onClick={() => setRules(DEFAULT_PROMOTION_RULES)}>
          Reset to default rules
        </button>
        <div className="flex items-center gap-3">
          {save.isSuccess && rules === saved && <span className="text-sm text-[var(--muted)]">Saved</span>}
          <Button variant="primary" disabled={save.isPending || rules === saved} onClick={() => save.mutate({ promotionRules: rules })}>
            Save rules
          </Button>
        </div>
      </div>
      {canTest && <TestRules companyRules={rules} />}
    </Card>
  );
}

const SHARE_LABEL = { none: "Kept private", group: "Shared with the group", company: "Shared with everyone" } as const;

/** Runs the rules (as typed, saved or not) over a few example memories. Nothing is shared. */
function TestRules({ companyRules }: { companyRules: string }) {
  const groups = useGroups();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("Acme Corp pays invoices 45 days after they arrive.\nI have a dentist appointment on Friday.\nThe sales team reviews the pipeline every Monday at 10.");
  const [groupId, setGroupId] = useState<string>("");
  const run = useMutation({
    mutationFn: () =>
      unwrap(
        api.admin.memory["test-rules"].$post({
          json: { facts: text.split("\n").map((l) => l.trim()).filter((l) => l.length >= 3).slice(0, 15), groupId: groupId || null, companyRules },
        }),
      ),
  });
  const lines = text.split("\n").filter((l) => l.trim().length >= 3).length;
  if (!open)
    return (
      <button type="button" className="mt-4 text-sm text-[var(--color-accent)] hover:underline" onClick={() => setOpen(true)}>
        Test the rules on example memories…
      </button>
    );
  return (
    <div className="mt-5 rounded-lg border border-[var(--line)] p-4">
      <h4 className="mb-1 text-sm font-semibold">Test the rules</h4>
      <p className="mb-3 text-xs text-[var(--muted)]">One example memory per line (up to 15). The rules above, as typed, decide each one the way they would for a person in the group you pick. Nothing is shared or saved.</p>
      <textarea rows={5} value={text} onChange={(e) => setText(e.target.value)} className={rulesBox} aria-label="Example memories, one per line" />
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <Select value={groupId} onChange={(e) => setGroupId(e.target.value)} className="w-auto" aria-label="As someone in">
          <option value="">As someone without a group</option>
          {groups.data?.map((g) => (
            <option key={g.id} value={g.id}>As someone in {g.name}</option>
          ))}
        </Select>
        <Button disabled={run.isPending || !lines || lines > 15} onClick={() => run.mutate()}>
          {run.isPending ? "Deciding…" : "Run the test"}
        </Button>
        {lines > 15 && <span className="text-xs text-red-700">At most 15 lines.</span>}
      </div>
      <ErrorText error={run.error} />
      {run.data && (
        <ul className="mt-3 divide-y divide-[var(--line)] text-sm">
          {run.data.off && <li className="py-2 text-[var(--muted)]">Sharing is off for this group, so nothing would be shared.</li>}
          {run.data.results.map((r, i) => (
            <li key={i} className="py-2.5">
              <div className="flex flex-wrap items-center gap-2">
                <Badge tone={r.share === "company" ? "good" : r.share === "group" ? "accent" : "neutral"}>{SHARE_LABEL[r.share]}</Badge>
                <span>{r.text}</span>
              </div>
              {r.sharedText && <div className="mt-1 text-xs text-[var(--muted)]">Would be shared as: “{r.sharedText}”</div>}
              {r.reason && <div className="mt-0.5 text-xs text-[var(--muted)]">Why: {r.reason}</div>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function SkillRules({ settings, groupsAllowed, onSaved }: { settings: { sharingEnabled: boolean; requireApproval: boolean; sensitivityRules: string }; groupsAllowed: number; onSaved: () => void }) {
  const [form, setForm] = useState(settings);
  const save = useMutation({ mutationFn: (json: typeof settings) => unwrap(api.admin.skills.settings.$put({ json })), onSuccess: onSaved });
  const rescan = useMutation({ mutationFn: () => unwrap(api.admin.skills.rescan.$post()), onSuccess: onSaved });
  const toggle = (p: Partial<typeof settings>) => {
    const next = { ...form, ...p };
    setForm(next);
    save.mutate({ ...settings, ...p });
  };
  return (
    <>
      <Card className="mb-4 p-5">
        <div className="grid gap-x-8 sm:grid-cols-2">
          <Switch
            label="Share people's skills"
            hint="Skills assistants create are checked for sensitive data, then given to colleagues' assistants: within the person's group or company-wide, as the group's level says."
            checked={form.sharingEnabled}
            disabled={save.isPending}
            onChange={(v) => toggle({ sharingEnabled: v })}
          />
          <Switch label="Review before sharing" hint="Clean skills wait under Skills → Needs review until you share them." checked={form.requireApproval} disabled={save.isPending} onChange={(v) => toggle({ requireApproval: v })} />
        </div>
        {form.sharingEnabled && groupsAllowed === 0 && <p className="mt-2 text-sm text-amber-800">No group shares skills yet: set a level for a group in the table below.</p>}
      </Card>
      <Card className="p-5">
        <h3 className="mb-1 text-sm font-semibold">Company rules for skills (sensitivity)</h3>
        <p className="mb-3 text-sm text-[var(--muted)]">
          Plain-language instructions for the AI that decides whether a person's skill may be shared at all. When unsure, it keeps the skill back. Credentials and the author's own saved secrets are always caught, whatever the rules say.
        </p>
        <textarea rows={10} value={form.sensitivityRules} onChange={(e) => setForm({ ...form, sensitivityRules: e.target.value })} className={rulesBox} aria-label="Company rules for skills" />
        <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap gap-4">
            <button type="button" className="text-sm text-[var(--color-accent)] hover:underline" onClick={() => setForm({ ...form, sensitivityRules: DEFAULT_SKILL_RULES })}>
              Reset to default rules
            </button>
            <button
              type="button"
              className="text-sm text-[var(--color-accent)] hover:underline disabled:opacity-50"
              disabled={rescan.isPending || form.sensitivityRules !== settings.sensitivityRules}
              title={form.sensitivityRules !== settings.sensitivityRules ? "Save the rules first" : undefined}
              onClick={() => rescan.mutate()}
            >
              {rescan.isSuccess ? "Re-checking in the background" : "Re-check every skill with the saved rules"}
            </button>
          </div>
          <Button variant="primary" disabled={save.isPending || form.sensitivityRules === settings.sensitivityRules} onClick={() => save.mutate({ ...settings, sensitivityRules: form.sensitivityRules })}>
            Save rules
          </Button>
        </div>
        <ErrorText error={save.error ?? rescan.error} />
      </Card>
    </>
  );
}

/** Every group's two levels, editable in place; the group's own rules are a link away. */
function GroupLevels() {
  const qc = useQueryClient();
  const groups = useGroups();
  const save = useMutation({
    mutationFn: ({ g, change }: { g: NonNullable<typeof groups.data>[number]; change: { promoteMemory?: ShareLevel; publishSkills?: ShareLevel } }) =>
      unwrap(
        api.admin.groups[":id"].knowledge.$put({
          param: { id: g.id },
          json: { promoteMemory: g.permissions.promoteMemory, publishSkills: g.permissions.publishSkills, memoryRules: g.knowledge.memoryRules, skillRules: g.knowledge.skillRules, ...change },
        }),
      ),
    onSuccess: () => {
      for (const k of ["groups", "memory", "skills-admin"]) void qc.invalidateQueries({ queryKey: [k] });
    },
  });
  if (groups.isPending) return <Spinner />;
  if (groups.error) return <ErrorText error={groups.error} />;
  if (!groups.data.length) return <Card className="p-5 text-sm text-[var(--muted)]">No groups yet.</Card>;
  return (
    <Card className="overflow-x-auto">
      <table className="w-full min-w-[560px] text-sm">
        <thead className="bg-[var(--panel-2)] text-left text-xs text-[var(--muted)]">
          <tr>
            <th className="px-4 py-2 font-medium">Group</th>
            <th className="px-4 py-2 font-medium">Memories</th>
            <th className="px-4 py-2 font-medium">Skills</th>
            <th className="px-4 py-2 font-medium">Group's own rules</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[var(--line)]">
          {groups.data.map((g) => (
            <tr key={g.id}>
              <td className="px-4 py-2.5 font-medium">{g.name}</td>
              {(["promoteMemory", "publishSkills"] as const).map((k) => (
                <td key={k} className="px-4 py-2">
                  <Select
                    value={g.permissions[k]}
                    disabled={save.isPending}
                    onChange={(e) => save.mutate({ g, change: { [k]: e.target.value as ShareLevel } })}
                    aria-label={`${g.name}: ${k === "promoteMemory" ? "memories" : "skills"}`}
                    className="h-8 w-auto"
                  >
                    {LEVELS.map((l) => (
                      <option key={l} value={l}>{LEVEL_LABEL[l]}</option>
                    ))}
                  </Select>
                </td>
              ))}
              <td className="px-4 py-2.5">
                <Link to={knowledgePath(g.id)} className="inline-flex items-center gap-1.5 whitespace-nowrap text-[var(--color-accent)] hover:underline">
                  Edit rules <Icon name="arrowRight" size={14} />
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="px-4 pb-3"><ErrorText error={save.error} /></div>
    </Card>
  );
}
